// Centralised helpers for the tenant `settings` blob.
//
// Every settings page (AI / Organization / Workspace / Branding / Account)
// reads from and writes to the same `/settings` endpoint, so we keep the
// fetch + merge + put dance in one place. Each page passes only the keys it
// owns; the merge step preserves siblings it doesn't touch.

import { buildApiUrl } from '../config';

export type TenantSettings = Record<string, any>;

export const getTenantIdFromStorage = (): string => {
  try {
    const t = JSON.parse(String(window.localStorage.getItem('klaris_tenant')));
    return String(t?.tenant_id || '');
  } catch {
    return '';
  }
};

const authHeaders = (): Record<string, string> => {
  const token = window.localStorage.getItem('klaris_jwt');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export async function fetchTenantSettings(tenantId: string): Promise<TenantSettings> {
  const resp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/settings`), {
    headers: authHeaders(),
    cache: 'no-store',
  } as RequestInit);
  const data = await resp.json();
  if (!resp.ok) throw new Error(data?.detail || 'Failed to load settings');
  return (data?.settings || {}) as TenantSettings;
}

/**
 * Shallow-merge `updates` into the latest server-side settings and PUT back.
 * Pass a function (`current => partial`) when you need to merge into a nested
 * object (e.g. `BRANDING`) without clobbering sibling fields.
 */
export async function saveTenantSettings(
  tenantId: string,
  updates: TenantSettings | ((current: TenantSettings) => TenantSettings),
): Promise<TenantSettings> {
  const current = await fetchTenantSettings(tenantId);
  const partial = typeof updates === 'function' ? updates(current) : updates;
  const merged = { ...current, ...partial };
  const resp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/settings`), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ settings: merged }),
  } as RequestInit);
  const data = await resp.json();
  if (!resp.ok) throw new Error(data?.detail || 'Failed to save settings');
  return merged;
}
